// src/components/Login.jsx
import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { agentAPI } from "../services/api";

function Login({ onLoginSuccess }) {
  const [activeTab, setActiveTab] = useState("email"); // 'email' or 'phone'
  const [step, setStep] = useState(1); // 1: Enter email/phone, 2: Enter OTP
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    otp: "",
    region: "vn",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleSuccess = async (tokenResponse) => {
    setLoading(true);
    setError("");
    try {
      const response = await agentAPI.googleTokenAuth({
        credential: tokenResponse.credential,
        region: formData.region,
      });
      const { success, token, role, email, name, userId, loginMethod } = response.data;
      if (!success || !token) {
        setError("Google login failed. Invalid response from server.");
        return;
      }
      if (!["AGENT", "ADMIN", "MANAGER"].includes(role)) {
        setError("Access denied. Only agents and admins can access this system.");
        return;
      }
      const userData = {
        _id: userId, userId,
        name: name || email || "User",
        email: email || "",
        role, loginMethod,
        region: formData.region,
      };
      onLoginSuccess(token, userData);
    } catch (err) {
      setError(err.response?.data?.message || "Google login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError("");
  };

const handleSendOTP = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError("");

  try {
    if (activeTab === "email") {
      const response = await agentAPI.sendEmailOTP({
        email: formData.email,
        region: formData.region,
      });

      if (response.data.success) {
        setStep(2);
        // Removed alert here for smooth UX
      }
    } else {
      const response = await agentAPI.sendPhoneOTP({
        phone: formData.phone,
        region: formData.region,
      });

      if (response.data.success) {
        setStep(2);
        // Removed alert here for smooth UX
      }
    }
  } catch (error) {
    console.error("Error sending OTP:", error);
    setError(
      error.response?.data?.message || "Failed to send OTP. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let response;

      if (activeTab === "email") {
        response = await agentAPI.verifyEmailOTP({
          email: formData.email,
          otp: formData.otp,
          region: formData.region,
        });
      } else {
        response = await agentAPI.verifyPhoneOTP({
          phone: formData.phone,
          otp: formData.otp,
          region: formData.region,
        });
      }

      // ✅ FIXED: Extract data from response properly
      const { success, token, role, email, phone, name, userId, loginMethod } = response.data;

      if (!success || !token) {
        setError("Login failed. Invalid response from server.");
        setLoading(false);
        return;
      }

      // ✅ FIXED: Check if user has AGENT or ADMIN role
      if (!["AGENT", "ADMIN"].includes(role)) {
        setError(
          "Access denied. Only agents and admins can access this system."
        );
        setLoading(false);
        return;
      }

      // ✅ FIXED: Create proper user object with all necessary fields
      const userData = {
        _id: userId,
        userId,
        name: name || email || phone || "User",
        email: email || "",
        phone: phone || "",
        role,
        loginMethod,
        region: formData.region
      };

      console.log("✅ Login successful:", userData);
      
      // Pass token and user data to parent
      onLoginSuccess(token, userData);
      
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError(
        error.response?.data?.message || "Invalid OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setFormData((prev) => ({ ...prev, otp: "" }));
    setError("");
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginCard}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>🎯 Agent Login</h1>
          <p style={styles.subtitle}>Honestlee Thailand - Agent Portal</p>
        </div>

        {/* Tabs */}
        {step === 1 && (
          <div style={styles.tabs}>
            <button
              onClick={() => setActiveTab("email")}
              style={{
                ...styles.tab,
                ...(activeTab === "email" ? styles.tabActive : {}),
              }}
            >
              📧 Email
            </button>
            <button
              onClick={() => setActiveTab("phone")}
              style={{
                ...styles.tab,
                ...(activeTab === "phone" ? styles.tabActive : {}),
              }}
            >
              📱 Phone
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Enter Email/Phone */}
        {step === 1 && (
          <form onSubmit={handleSendOTP} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Region</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="th">🇹🇭 Thailand</option>
                <option value="ae">🇦🇪 UAE</option>
                <option value="in">🇮🇳 India</option>
              </select>
            </div>

            {activeTab === "email" ? (
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="agent@honestlee.com"
                  autoComplete="email"
                />
              </div>
            ) : (
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="+66821234567"
                  autoComplete="tel"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitButton,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "⏳ Sending..." : "📨 Send OTP"}
            </button>

            <p style={styles.hint}>
              You'll receive a 6-digit verification code
            </p>
          </form>
        )}

        {/* Step 2: Enter OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP} style={styles.form}>
            <div style={styles.otpInfo}>
              <p style={styles.otpText}>
                {activeTab === "email"
                  ? `Enter the OTP sent to ${formData.email}`
                  : `Enter the OTP sent to ${formData.phone}`}
              </p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Verification Code</label>
              <input
                type="text"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                required
                maxLength={6}
                style={styles.otpInput}
                placeholder="000000"
                autoComplete="one-time-code"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleBackToStep1}
                style={styles.backButton}
              >
                ← Back
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.submitButton,
                  opacity: loading ? 0.6 : 1,
                  flex: 1,
                }}
              >
                {loading ? "⏳ Verifying..." : "✅ Verify & Login"}
              </button>
            </div>

            <button
              type="button"
              onClick={handleSendOTP}
              disabled={loading}
              style={styles.resendButton}
            >
              Resend OTP
            </button>
          </form>
        )}

        {/* Google Sign-In — shown on step 1 only */}
        {step === 1 && (
          <div style={{ padding: "0 30px 24px" }}>
            <div style={styles.divider}>
              <span style={styles.dividerText}>or</span>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Google sign-in was cancelled or failed.")}
                text="continue_with"
                shape="rectangular"
                size="large"
                width="360"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>🔒 Secure agent authentication</p>
          <p style={styles.footerNote}>
            Only authorized agents and admins can access this portal
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "20px",
  },
  loginCard: {
    background: "white",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    maxWidth: "450px",
    width: "100%",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "40px 30px",
    textAlign: "center",
    color: "white",
  },
  title: {
    fontSize: "32px",
    marginBottom: "8px",
    fontWeight: "700",
  },
  subtitle: {
    fontSize: "16px",
    opacity: 0.9,
  },
  tabs: {
    display: "flex",
    padding: "0",
    borderBottom: "2px solid #E2E8F0",
  },
  tab: {
    flex: 1,
    padding: "16px",
    background: "white",
    border: "none",
    fontSize: "16px",
    fontWeight: "600",
    color: "#718096",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  tabActive: {
    color: "#667eea",
    borderBottom: "3px solid #667eea",
    background: "#F7FAFC",
  },
  form: {
    padding: "30px",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#2d3748",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #E2E8F0",
    borderRadius: "10px",
    fontSize: "16px",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #E2E8F0",
    borderRadius: "10px",
    fontSize: "16px",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  otpInput: {
    width: "100%",
    padding: "20px",
    border: "2px solid #E2E8F0",
    borderRadius: "10px",
    fontSize: "32px",
    textAlign: "center",
    letterSpacing: "10px",
    fontWeight: "600",
    fontFamily: "monospace",
    boxSizing: "border-box",
  },
  submitButton: {
    width: "100%",
    padding: "16px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  buttonGroup: {
    display: "flex",
    gap: "12px",
  },
  backButton: {
    padding: "16px 24px",
    background: "#E2E8F0",
    color: "#2d3748",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
  resendButton: {
    marginTop: "12px",
    width: "100%",
    padding: "12px",
    background: "transparent",
    color: "#667eea",
    border: "2px solid #667eea",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  hint: {
    marginTop: "12px",
    textAlign: "center",
    fontSize: "13px",
    color: "#718096",
  },
  otpInfo: {
    marginBottom: "25px",
    padding: "15px",
    background: "#EBF8FF",
    borderRadius: "10px",
    textAlign: "center",
  },
  otpText: {
    fontSize: "14px",
    color: "#2C5282",
    margin: 0,
  },
  errorBox: {
    margin: "0 30px 0",
    padding: "15px",
    background: "#FED7D7",
    border: "2px solid #FC8181",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    color: "#C53030",
  },
  errorIcon: {
    fontSize: "18px",
  },
  footer: {
    padding: "20px 30px",
    background: "#F7FAFC",
    textAlign: "center",
    borderTop: "1px solid #E2E8F0",
  },
  footerText: {
    fontSize: "13px",
    color: "#4A5568",
    marginBottom: "5px",
    margin: "0 0 5px 0",
  },
  footerNote: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "0 0 16px",
    gap: "12px",
    color: "#CBD5E0",
    fontSize: "13px",
  },
  dividerText: {
    flex: 1,
    textAlign: "center",
    borderTop: "1px solid #E2E8F0",
    lineHeight: "0",
    margin: "0 8px",
    color: "#A0AEC0",
    fontSize: "12px",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  googleButton: {
    width: "100%",
    padding: "14px 20px",
    background: "white",
    border: "1.5px solid #E2E8F0",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#2d3748",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    transition: "all 0.2s",
  },
};

export default Login;
